import React from 'react';
import { createPortal } from 'react-dom';
import styled from 'styled-components';

interface ConfirmModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title: string | string[] | React.ReactNode;
  confirmText?: string;
  cancelText?: string;
  icon?: string;
  iconBackground?: string;
  onCancel?: () => void; // 취소 버튼 클릭 시 호출되는 함수 (onClose 대신)
  /** true면 확인 버튼 비활성화 및 클릭 무시 */
  confirmDisabled?: boolean;
}

const ConfirmModal: React.FC<ConfirmModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  title,
  confirmText = '확인',
  cancelText = '취소',
  icon = '!',
  iconBackground = 'var(--status-exempted)',
  onCancel,
  confirmDisabled = false
}) => {
  if (!isOpen) return null;

  if (typeof document === 'undefined') return null;

  const handleOverlayClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    onClose();
  };

  const handleContainerClick = (e: React.MouseEvent) => {
    e.stopPropagation();
  };

  const handleCancelClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (onCancel) {
      onCancel();
    } else {
      onClose();
    }
  };

  const handleConfirmClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (confirmDisabled) return;
    onConfirm();
  };

  return createPortal(
    <>
      <Overlay onClick={handleOverlayClick} />
      <Container onClick={handleContainerClick}>
        <Content>
          <Icon $background={iconBackground}>{icon}</Icon>
          <Title>
            {typeof title === 'string' || Array.isArray(title) ? (
              Array.isArray(title) ? (
                title.map((line, index) => (
                  <React.Fragment key={index}>
                    {line}
                    {index < title.length - 1 && <br />}
                  </React.Fragment>
                ))
              ) : (
                title
              )
            ) : (
              title
            )}
          </Title>
        </Content>
        <Footer>
          <CancelButton type="button" data-variant="modal-secondary" onClick={handleCancelClick}>
            {cancelText}
          </CancelButton>
          <ConfirmButton type="button" disabled={confirmDisabled} onClick={handleConfirmClick}>
            {confirmText}
          </ConfirmButton>
        </Footer>
      </Container>
    </>,
    document.body
  );
};

export default ConfirmModal;

// 스타일 컴포넌트들
const Overlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  z-index: 30000;
`;

const Container = styled.div`
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  background: var(--color-surface);
  border-radius: 16px;
  box-shadow: var(--modal-shadow-large);
  border: var(--modal-border);
  z-index: 30001;
  width: 90vw;
  max-width: 400px;
  overflow: hidden;
  
  @media (max-width: 480px) {
    width: 95vw;
  }
`;

const Content = styled.div`
  padding: 32px 24px 24px;
  text-align: center;
`;

const Icon = styled.div<{ $background: string }>`
  width: 60px;
  height: 60px;
  background: ${props => props.$background};
  color: white;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 32px;
  font-weight: bold;
  margin: 0 auto 20px;
`;

const Title = styled.h3`
  margin: 0 0 12px 0;
  font-size: 18px;
  font-weight: 600;
  color: var(--color-text-primary);
  line-height: 1.4;
`;

const Footer = styled.div`
  display: flex;
  gap: 12px;
  padding: 0 24px 24px;
`;

const CancelButton = styled.button`
  flex: 1;
  padding: 12px 24px;
  background: var(--color-surface);
  color: var(--color-text-secondary);
  border: 1px solid var(--color-border);
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: var(--color-panel);
    border-color: var(--color-text-muted);
  }

  &:active {
    transform: translateY(0);
  }

  &:focus-visible {
    outline: 2px solid #000;
    outline-offset: 2px;
  }
`;

const ConfirmButton = styled.button`
  flex: 1;
  padding: 12px 24px;
  background: var(--color-accent-strong);
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover:not(:disabled) {
    background: var(--color-accent);
    transform: translateY(-1px);
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
  }

  &:active:not(:disabled) {
    transform: translateY(0);
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  }

  &:disabled {
    opacity: 0.55;
    cursor: not-allowed;
    transform: none;
    box-shadow: none;
  }

  &:focus-visible {
    outline: 2px solid #000;
    outline-offset: 2px;
  }
`; 